import { Box3, Euler, Object3D, PerspectiveCamera, Quaternion, Vector3 } from "three";

import {
  moveWithCollisions,
  type CollisionBody,
  type VisitorCapsule,
  VISITOR_CAPSULE,
} from "./collision";

const UP = new Vector3(0, 1, 0);
const PRESET_MIN_DISTANCE = 3.2;
const PRESET_MAX_DISTANCE = 5.4;
const PRESET_FRAME_MARGIN = 1.35;

export class FirstPersonController {
  readonly #camera: PerspectiveCamera;
  readonly #colliders: readonly CollisionBody[];
  readonly #capsule: VisitorCapsule;
  readonly #spawnPosition: Vector3;
  readonly #spawnQuaternion: Quaternion;
  readonly #keys = new Set<string>();
  #enabled = false;
  #yaw = 0;
  #pitch = 0;
  #touchX = 0;
  #touchForward = 0;

  constructor(
    camera: PerspectiveCamera,
    colliders: readonly CollisionBody[],
    capsule: VisitorCapsule = VISITOR_CAPSULE,
  ) {
    this.#camera = camera;
    this.#colliders = colliders;
    this.#capsule = capsule;
    this.#spawnPosition = camera.position.clone();
    this.#spawnQuaternion = camera.quaternion.clone();
    this.#syncAngles();
  }

  get enabled(): boolean {
    return this.#enabled;
  }

  setEnabled(enabled: boolean): void {
    this.#enabled = enabled;
    if (!enabled) this.clearInput();
  }

  setKey(code: string, pressed: boolean): void {
    if (pressed) this.#keys.add(code);
    else this.#keys.delete(code);
  }

  setTouchMovement(sideways: number, forward: number): void {
    this.#touchX = Math.max(-1, Math.min(1, sideways));
    this.#touchForward = Math.max(-1, Math.min(1, forward));
  }

  look(deltaX: number, deltaY: number): void {
    if (!this.#enabled) return;
    this.#yaw -= deltaX * 0.002;
    this.#pitch -= deltaY * 0.002;
    const limit = (85 * Math.PI) / 180;
    this.#pitch = Math.max(-limit, Math.min(limit, this.#pitch));
    this.#camera.rotation.set(this.#pitch, this.#yaw, 0, "YXZ");
  }

  update(deltaSeconds: number): void {
    if (!this.#enabled) return;
    const delta = Math.min(Math.max(deltaSeconds, 0), 1 / 30);
    const forwardInput =
      Number(this.#keys.has("KeyW") || this.#keys.has("ArrowUp")) -
      Number(this.#keys.has("KeyS") || this.#keys.has("ArrowDown")) +
      this.#touchForward;
    const sidewaysInput =
      Number(this.#keys.has("KeyD") || this.#keys.has("ArrowRight")) -
      Number(this.#keys.has("KeyA") || this.#keys.has("ArrowLeft")) +
      this.#touchX;
    const input = new Vector3(sidewaysInput, 0, -forwardInput);
    if (input.lengthSq() === 0) return;
    input.normalize().multiplyScalar(1.6 * delta);
    const forward = new Vector3(0, 0, -1).applyQuaternion(this.#camera.quaternion);
    forward.y = 0;
    forward.normalize();
    const right = new Vector3().crossVectors(forward, UP).normalize();
    const displacement = right.multiplyScalar(input.x).add(forward.multiplyScalar(-input.z));
    const moved = moveWithCollisions(
      this.#camera.position,
      displacement,
      this.#colliders,
      this.#capsule,
    );
    moved.y = this.#spawnPosition.y;
    this.#camera.position.copy(moved);
  }

  reset(): void {
    this.#camera.position.copy(this.#spawnPosition);
    this.#camera.quaternion.copy(this.#spawnQuaternion);
    this.clearInput();
    this.#syncAngles();
  }

  navigateTo(position: Vector3, target: Vector3): void {
    this.#camera.position.set(position.x, this.#spawnPosition.y, position.z);
    this.#camera.lookAt(target);
    this.#camera.rotation.order = "YXZ";
    this.clearInput();
    this.#syncAngles();
    // Preset navigation is synchronous. Keep projection/raycast matrices in
    // sync immediately so a label can be tapped before the next render frame.
    this.#camera.updateMatrixWorld(true);
  }

  navigateToArtwork(routeStop: Vector3, painting: Object3D, label?: Object3D): void {
    const paintingTarget = painting.getWorldPosition(new Vector3());
    const bounds = new Box3().setFromObject(painting);
    if (label !== undefined) bounds.union(new Box3().setFromObject(label));
    const target = bounds.getCenter(new Vector3());
    const size = bounds.getSize(new Vector3());
    const verticalFov = (this.#camera.fov * Math.PI) / 180;
    const horizontalFov = 2 * Math.atan(Math.tan(verticalFov / 2) * this.#camera.aspect);
    const artworkWidth = Math.max(size.x, size.z);
    const frameDistance =
      Math.max(
        size.y / (2 * Math.tan(verticalFov / 2)),
        artworkWidth / (2 * Math.tan(horizontalFov / 2)),
      ) * PRESET_FRAME_MARGIN;
    const direction = routeStop.clone().sub(paintingTarget);
    direction.y = 0;
    if (direction.lengthSq() === 0) throw new Error(`NAVIGATION_PRESET_INVALID: ${painting.name}`);
    direction.normalize();
    const routeDistance = Math.hypot(
      routeStop.x - paintingTarget.x,
      routeStop.z - paintingTarget.z,
    );
    const distance = Math.min(
      PRESET_MAX_DISTANCE,
      Math.max(PRESET_MIN_DISTANCE, routeDistance, frameDistance),
    );
    this.navigateTo(target.clone().addScaledVector(direction, distance), target);
  }

  clearInput(): void {
    this.#keys.clear();
    this.#touchX = 0;
    this.#touchForward = 0;
  }

  #syncAngles(): void {
    const euler = new Euler().setFromQuaternion(this.#camera.quaternion, "YXZ");
    this.#pitch = euler.x;
    this.#yaw = euler.y;
    this.#camera.rotation.order = "YXZ";
  }
}
